// Single source of truth for the public site origin. NEXT_PUBLIC_SITE_URL may
// override it per environment; the fallback IS the real production domain so a
// missing env var can never send canonical/hreflang/sitemap to a bogus host.
// A build-time guard in next.config.mjs rejects placeholder/invalid values.
export const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, '') ?? 'https://yatfliz.pl';

// Locale routing: Polish is the default (no prefix); others are path-prefixed.
// The "ua" route segment is Ukrainian — its hreflang code is "uk".
export const LOCALES = ['pl', 'en', 'ua', 'ru'] as const;
export type Locale = (typeof LOCALES)[number];

/** Path for a locale's home (pl = "/", others = "/<locale>"). */
export const localePath = (locale: Locale) => (locale === 'pl' ? '' : `/${locale}`);

/** hreflang code per route segment ("ua" segment is Ukrainian → "uk"). */
export const HREFLANG: Record<Locale, string> = { pl: 'pl', en: 'en', ua: 'uk', ru: 'ru' };

// All 18 official Warsaw districts — the service area, enumerated for
// district-level relevance in the JSON-LD areaServed and llms.txt. Proper
// nouns; no localization needed.
export const WARSAW_DISTRICTS = [
  'Mokotów', 'Wola', 'Ursynów', 'Śródmieście', 'Żoliborz', 'Ochota',
  'Praga-Południe', 'Praga-Północ', 'Bemowo', 'Bielany', 'Białołęka',
  'Targówek', 'Wawer', 'Wilanów', 'Ursus', 'Włochy', 'Rembertów', 'Wesoła',
] as const;

/**
 * Relative metadata `alternates.languages` map for a path suffix. Reuse across
 * pages so hreflang stays consistent. e.g. altLanguages('/privacy') →
 * { pl:'/privacy', en:'/en/privacy', uk:'/ua/privacy', ru:'/ru/privacy', 'x-default':'/privacy' }.
 */
export function altLanguages(suffix = ''): Record<string, string> {
  const path = (locale: Locale) => `${localePath(locale)}${suffix}` || '/';
  const languages: Record<string, string> = { 'x-default': path('pl') };
  for (const locale of LOCALES) languages[HREFLANG[locale]] = path(locale);
  return languages;
}
